import { useEffect } from 'react'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import Footer from '../components/Footer'

export default function Aup() {
  useEffect(() => {
    document.body.classList.add('v2-legal-page')
    return () => document.body.classList.remove('v2-legal-page')
  }, [])

  return (
    <>
      <SEO
        title="Acceptable Use Policy - tokendance.net"
        description="tokendance.net acceptable use policy covering prohibited content, prohibited conduct, resource usage, and AI agent behavior on the platform."
        path="/aup"
      />

      <div className="od-home od-legal-shell">
        <SiteNav />

        <main className="od-legal-main">
          <header className="od-legal-hero">
            <h1>Acceptable Use Policy</h1>
            <p>Last updated: August 24, 2026</p>
          </header>

          <div className="od-legal-sections">
            <section className="od-legal-section">
              <h2>1. Overview</h2>
              <p>
                This Acceptable Use Policy ("Policy") governs your use of the tokendance.net platform ("Service"). It applies to all users — visitors, registered users, paying subscribers — and to any AI coding agents acting on your behalf with your tokens. This Policy is incorporated into our <a href="/terms">Terms of Service</a>. By using the Service, you agree to comply with it. Actions taken by an agent holding your credentials are treated as your actions.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>2. Prohibited Content</h2>
              <p>You may not use the Service to host, deploy, distribute, or transmit content that:</p>
              <ul>
                <li>Violates any applicable law or regulation in your jurisdiction or ours.</li>
                <li>Contains malware, ransomware, spyware, viruses, or any other malicious code.</li>
                <li>Facilitates phishing, credential harvesting, or impersonation of any person, organization, or service.</li>
                <li>Infringes the intellectual property rights of others, including pirated software, media, or unlicensed content distribution.</li>
                <li>Contains child sexual abuse material (CSAM) or sexualizes minors in any form. We report such content to the relevant authorities.</li>
                <li>Promotes violence, terrorism, or harm against any individual or group, or constitutes harassment, threats, or hate speech.</li>
                <li>Distributes non-consensual intimate imagery or content intended to dox, stalk, or endanger a private individual.</li>
                <li>Is deceptive or fraudulent, including fake reviews, fabricated records, scam storefronts, or deceptive investment schemes.</li>
              </ul>
            </section>

            <section className="od-legal-section">
              <h2>3. Prohibited Conduct</h2>
              <p>You may not use the Service — directly, or via an agent acting on your behalf — to:</p>
              <ul>
                <li>Attempt to gain unauthorized access to other users' deployments, data, accounts, or any part of our infrastructure.</li>
                <li>Probe, scan, or test the vulnerability of any system or network without authorization.</li>
                <li>Conduct denial-of-service attacks, or otherwise disrupt or degrade the Service or any third-party service.</li>
                <li>Send unsolicited bulk communications (spam), whether by email, messaging platforms, or any other channel.</li>
                <li>Operate open proxies, open relays, or anonymization endpoints designed to disguise abusive traffic.</li>
                <li>Scrape third-party services in violation of their terms, rate limits, or robots.txt directives.</li>
                <li>Interfere with, circumvent, or attempt to circumvent our rate limits, abuse controls, spend caps, or billing systems.</li>
                <li>Resell, sublicense, or provide the Service to third parties in a way that masks the true origin of usage, without a written agreement.</li>
              </ul>
            </section>

            <section className="od-legal-section">
              <h2>4. Resource Usage</h2>
              <ul>
                <li>Cryptocurrency mining is prohibited without explicit prior written authorization.</li>
                <li>Deployments must not be used primarily as bulk storage, backup targets, or content delivery for assets unrelated to a deployed application.</li>
                <li>Sustained abnormal consumption of compute, bandwidth, or AI gateway tokens that degrades the Service for others may be throttled or suspended, whether initiated by you or by an agent holding your token.</li>
                <li>Free tier and promotional resources are for evaluation and personal projects, not for automated farming across multiple accounts.</li>
              </ul>
            </section>

            <section className="od-legal-section">
              <h2>5. AI Agent Behavior</h2>
              <p>
                tokendance.net is built to be operated by AI coding agents, and the same rules apply to them. As the account holder, you are responsible for constraining your agents:
              </p>
              <ul>
                <li>Agents must not be configured or instructed to evade rate limits, abuse detection, spend caps, or approval prompts.</li>
                <li>Agents must not use your tokens to perform any activity prohibited by this Policy. Agent-initiated violations are treated as account-holder violations.</li>
                <li>You must not issue tokens to agents or automation you do not control or cannot revoke.</li>
                <li>If you suspect an agent is misbehaving — acting outside its instructions, draining spend, or exfiltrating data — revoke the credential immediately and contact us at <a href="mailto:hi@tokendance.net">hi@tokendance.net</a>.</li>
              </ul>
            </section>

            <section className="od-legal-section">
              <h2>6. Security Research</h2>
              <p>
                We welcome good-faith security research on your own deployments and on the platform within reasonable bounds: no service disruption, no access to other users' data, and prompt disclosure of findings to <a href="mailto:hi@tokendance.net">hi@tokendance.net</a>. Testing that targets other users, degrades the Service, or exfiltrates data you do not own is prohibited regardless of intent.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>7. Reporting Abuse</h2>
              <p>
                If you encounter content or behavior on tokendance.net that violates this Policy, report it to <a href="mailto:hi@tokendance.net">hi@tokendance.net</a> with the deployment URL and a description of the issue. We review reports promptly and take action where warranted.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>8. Enforcement</h2>
              <p>
                We may investigate suspected violations of this Policy and may, at our discretion and with or without prior notice: remove or disable access to content, throttle or suspend deployments, revoke agent tokens, suspend or terminate accounts, and report unlawful activity to law enforcement. Where practical, we will notify you and give you an opportunity to remedy a violation — but we are not required to do so for serious violations, including illegal content, active attacks, or abuse that endangers others.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>9. Changes to This Policy</h2>
              <p>
                We may update this Policy from time to time. Material changes will be communicated via email or a prominent notice on our website. Your continued use of the Service after changes become effective constitutes acceptance of the revised Policy.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>10. Contact Us</h2>
              <p>
                If you have questions about this Acceptable Use Policy, please contact us at <a href="mailto:hi@tokendance.net">hi@tokendance.net</a>.
              </p>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  )
}
